import { useEffect, useState } from "react";
import { OrangeQontactLogo } from "@components/OrangeQontactLogo";
import { ComboBox } from "@shared/designSystem/ComboBox";
import { HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { LeftMenuOption } from "@shared/designSystem/LeftMenuOption";
import { LeftMenuSection } from "@shared/designSystem/LeftMenuSection";
import { ScrollFadeContainer } from "@shared/designSystem/ScrollFadeContainer";
import UserCard from "@shared/designSystem/UserCard";
import { designSystemStyles } from "@shared/styles/designSystemStyles";
import { useAuth } from "@contexts/authContext";
import { usePopup } from "@contexts/popupContext";
import { LiteVersionPopup } from "@components/General/ProhibitedPopups";
import OrganizationSelectorModalScreen from "@screens/Organizations/OrganizationSelectorModalScreen";
import type { Organization } from "@models/Organization";
import { useSidebarScreenViewModel } from "./SidebarScreenViewModel";

const SIDEBAR_WIDTH = "320px";
const SIDEBAR_BUTTONS_WIDTH = "296px";

interface SidebarScreenProps {
    organization: Organization | undefined;
    onOrganizationSelected: (organization: Organization) => void;
}

export const SidebarScreen: React.FC<SidebarScreenProps> = ({
    organization,
    onOrganizationSelected,
}) => {
    const { orgs, user, dashboardUser, isSystemAdmin } = useAuth();
    const { showPopup } = usePopup();
    const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);

    const {
        sections,
        toggleSection,
        bottomOptions,
        comboOptions,
        comboValue,
        selectComboOption,
        selectOption,
        openProfile,
        userCard,
        isSidebarDisabled,
    } = useSidebarScreenViewModel({
        organization,
        organizations: orgs,
        user,
        dashboardUser,
        isSystemAdmin,
        onOrganizationSelected,
        onLockedOptionSelected: () => showPopup(() => <LiteVersionPopup />),
    });

    useEffect(() => {
        const handleKeyboardShortcut = (event: KeyboardEvent) => {
            const isKKey = event.key.toLowerCase() === 'k';
            const isMacPlatform = navigator.platform.toLowerCase().includes('mac');
            const isShortcut = isMacPlatform
                ? event.metaKey && !event.ctrlKey
                : event.ctrlKey && !event.metaKey;
            const target = event.target as HTMLElement | null;
            const isTypingElement = target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName || '');

            if (!isKKey || !isShortcut || event.altKey || event.shiftKey || isTypingElement) {
                return;
            }

            if ((orgs?.length ?? 0) <= 4) {
                return;
            }

            event.preventDefault();
            setIsOrgModalOpen(true);
        };

        window.addEventListener('keydown', handleKeyboardShortcut);
        return () => {
            window.removeEventListener('keydown', handleKeyboardShortcut);
        };
    }, [orgs?.length]);

    const menuContainerStyle = {
        opacity: isSidebarDisabled ? 0.6 : 1,
        pointerEvents: isSidebarDisabled ? "none" as const : "auto" as const,
        transition: designSystemStyles.opacityTransitionEase,
    };

    return (
        <VerticalContainer
            gap={designSystemStyles.gapLg}
            height="100%"
            width={SIDEBAR_WIDTH}
            maxWidth={SIDEBAR_WIDTH}
            paddingLeft={designSystemStyles.paddingSm}
            paddingRight={designSystemStyles.paddingSm}
            backgroundColor={designSystemStyles.colorBackgroundWhite}
        >
            <HorizontalContainer
                width="187px"
                paddingTop={designSystemStyles.paddingLg}
                paddingBottom={designSystemStyles.paddingLg}
            >
                <OrangeQontactLogo />
            </HorizontalContainer>
            <ComboBox
                options={comboOptions}
                value={comboValue}
                placeholder="Seleccionar organización"
                maxVisibleOptions={4}
                iconType="building"
                onChange={selectComboOption}
                disabled={isSidebarDisabled}
            />
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: designSystemStyles.gapLg }}>
                <div style={{ flex: 1, minHeight: 0, ...menuContainerStyle }}>
                    <ScrollFadeContainer>
                        {sections.map((section) => (
                            <VerticalContainer
                                key={section.key}
                                width={SIDEBAR_BUTTONS_WIDTH}
                            >
                                <LeftMenuSection
                                    title={section.title}
                                    hiddenCount={section.hiddenCount}
                                    isOpen={section.isOpen}
                                    onToggle={() => toggleSection(section.key)}
                                >
                                    {section.options.map((option) => (
                                        <LeftMenuOption
                                            key={option.key}
                                            label={option.label}
                                            icon={option.icon}
                                            isActive={option.isActive}
                                            isLocked={option.isLocked}
                                            onClick={isSidebarDisabled ? undefined : () => selectOption(option)}
                                        />
                                    ))}
                                </LeftMenuSection>
                            </VerticalContainer>
                        ))}
                    </ScrollFadeContainer>
                </div>
                <div style={menuContainerStyle}>
                    <VerticalContainer
                        justifyContent="flex-end"
                        paddingBottom={designSystemStyles.paddingLg}
                        gap={designSystemStyles.gapSm}
                    >
                        {bottomOptions.map((option) => (
                            <LeftMenuOption
                                key={option.key}
                                label={option.label}
                                icon={option.icon}
                                isActive={option.isActive}
                                isLocked={option.isLocked}
                                onClick={isSidebarDisabled ? undefined : () => selectOption(option)}
                            />
                        ))}
                        <UserCard
                            name={userCard.name}
                            subtitle={userCard.subtitle}
                            imageURL={userCard.imageURL}
                            width={SIDEBAR_BUTTONS_WIDTH}
                            onClick={isSidebarDisabled ? undefined : openProfile}
                        />
                    </VerticalContainer>
                </div>
            </div>
            <OrganizationSelectorModalScreen
                isOpen={isOrgModalOpen}
                organizations={orgs ?? []}
                selectedOrganization={organization}
                onClose={() => setIsOrgModalOpen(false)}
                onOrganizationSelected={(selectedOrganization) => {
                    onOrganizationSelected(selectedOrganization);
                    setIsOrgModalOpen(false);
                }}
            />
        </VerticalContainer>
    );
};

export default SidebarScreen;
